import cx from "clsx"
import { Card, Flex, Icon, Skeleton, Text } from "components/common"
import { t } from "i18next"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FaPlay, FaSpinner } from "react-icons/fa"
import styles from "./index.module.less"

const VIDEO_CONSTANTS = {
  MAX_HEIGHT: 180,
  MIN_WIDTH: 180,
  OVERLAY_Z_INDEX: 1000,
  CLOSE_BUTTON_SIZE: 24,
  PLAY_BUTTON_SIZE: 28,
  BORDER_RADIUS: "inherit"
}

function WaVideo({ parsedContent }) {
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true) // Start with loading true
  const [hasError, setHasError] = useState(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [supportsPiP, setSupportsPiP] = useState(false)
  
  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const thumbnailRef = useRef(null)

  // Memoized parsed content to prevent unnecessary re-renders
  const videoData = useMemo(() => ({
    url: parsedContent?.s3KeyValue || "",
    thumbnail: parsedContent?.thumbnail || "",
    mimeType: parsedContent?.mimeType || "video/mp4",
    caption: parsedContent?.caption || ""
  }), [parsedContent])



  // Optimized event handlers with useCallback
  const handleVideoLoad = useCallback(() => {
    setIsVideoLoaded(true)
    setIsLoading(false)
    setHasError(false)
  }, [])

  const handleVideoError = useCallback(() => {
    setHasError(true)
    setIsLoading(false)
    setIsVideoLoaded(false)
  }, [])

  const handleVideoLoadStart = useCallback(() => {
    setIsLoading(true)
    setHasError(false)
  }, [])

  const handleOpenPlayer = useCallback(() => {
    setIsPlayerOpen(true)
    setIsLoading(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsPlayerOpen(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [])

  const handlePictureInPicture = useCallback(async () => {
    if (videoRef.current && supportsPiP) {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture()
        } else {
          await videoRef.current.requestPictureInPicture()
        }
      } catch (error) {}
    }
  }, [supportsPiP])
  
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.preventDefault()
      e.stopPropagation()
      handleClose()
    } else if (e.key === "p" || e.key === "P") {
      e.preventDefault()
      e.stopPropagation()
      handlePictureInPicture()
    }
  }, [handleClose, handlePictureInPicture])

  const handlePlayerClick = useCallback((e) => {
    e.stopPropagation()
  }, [])

  // Check Picture-in-Picture support
  useEffect(() => {
    setSupportsPiP(document.pictureInPictureEnabled && !videoRef.current?.disablePictureInPicture)
  }, [])

  // Focus management effect
  useEffect(() => {
    if (isPlayerOpen && playerRef.current) {
      playerRef.current.focus()
    }
  }, [isPlayerOpen])

  // Body scroll lock when player is open
  useEffect(() => {
    if (isPlayerOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = '15px' // Prevent layout shift
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
    
    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [isPlayerOpen])

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!thumbnailRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is in viewport, can start preloading
            const video = entry.target
            if (video.readyState === 0) {
              video.load()
            }
          }
        })
      },
      { 
        threshold: 0.1,
        rootMargin: '50px'
      }
    )

    observer.observe(thumbnailRef.current)

    return () => {
      observer.disconnect()
    }
  }, [videoData.url])

  // Cleanup effect for better memory management
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause()
      }
    }
  }, [])

  // Handle Escape key globally when player is open
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Escape' && isPlayerOpen) {
        handleClose()
      }
    }

    if (isPlayerOpen) {
      document.addEventListener('keydown', handleGlobalKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isPlayerOpen, handleClose])

  // Error state component
  if (!videoData.url) {
    return (
      <Card className={styles.mediaCard}>
        <Flex 
          align="center" 
          justify="center" 
          className={cx(styles.errorState, styles.videoUnavailable)}
        >
          <Icon icon="HiVideoCamera" className={cx(styles.errorIcon, styles.unavailable)} />
          <Text type="secondary">{t("Video unavailable")}</Text>
        </Flex>
      </Card>
    )
  }

  if (hasError) {
    return (
      <Card className={styles.mediaCard}>
        <Flex 
          align="center" 
          justify="center" 
          className={cx(styles.errorState, styles.videoError)}
        >
          <Icon icon="HiExclamationTriangle" className={cx(styles.errorIcon, styles.error)} />
          <Text type="secondary" className={cx(styles.errorText, styles.error)}>{t("Failed to load video")}</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <>
      <div className={styles.mediaCard}>
        <div 
          className={styles.videoContainer}
          role="button"
          tabIndex={0}
          aria-label={`Play video${videoData.caption ? `: ${videoData.caption}` : ''}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              handleOpenPlayer()
            }
          }}
          onClick={handleOpenPlayer}
        >
          {isLoading && !isVideoLoaded && (
            <Skeleton 
              active
              type="image"
              style={{
                width: "100%",
                height: "200px",
                borderRadius: "14px",
                minWidth: "280px"
              }}
            />
          )}
          <video 
            ref={thumbnailRef}
            src={videoData.url} 
            poster={videoData.thumbnail} 
            preload="metadata"
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
            onLoadStart={handleVideoLoadStart}
            aria-hidden="true"
            className={cx(styles.videoThumbnail, isVideoLoaded ? styles.loaded : styles.loading)}
            style={{
              display: isVideoLoaded ? "block" : "none",
              minWidth: "280px"
            }}
          />
          {isLoading && isVideoLoaded && (
            <div className={styles.loadingOverlay}>
              <FaSpinner 
                size={VIDEO_CONSTANTS.PLAY_BUTTON_SIZE} 
                style={{ 
                  color: "white",
                  animation: "spin 1s linear infinite"
                }} 
              />
            </div>
          )}

          {!isLoading && isVideoLoaded && (
            <button 
              className={styles.playOverlay} 
              onClick={handleOpenPlayer} 
              type="button"
              aria-label="Play video"
              style={{
                opacity: isVideoLoaded ? 1 : 0.7
              }}
            >
              <FaPlay 
                size={VIDEO_CONSTANTS.PLAY_BUTTON_SIZE} 
                style={{ verticalAlign: "middle" }} 
              />
            </button>
          )}
        </div>
        
        {videoData.caption && (
          <Text 
            type="secondary" 
            className={cx(styles.caption, isVideoLoaded ? styles.loaded : styles.loading)}
          >
            {videoData.caption}
          </Text>
        )}
      </div>

      {isPlayerOpen && createPortal(
        <div
          ref={playerRef}
          tabIndex={-1}
          className={styles.videoPlayer}
          onClick={handleClose}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <Icon 
            icon="IoMdClose" 
            onClick={handleClose} 
            className={styles.closeButton}
            role="button"
            aria-label="Close video player"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClose()
              }
            }}
          />
          {supportsPiP && (
            <Icon 
              icon="BiPictureInPicture" 
              onClick={handlePictureInPicture} 
              className={styles.pipButton}
              role="button"
              aria-label="Picture in Picture (Press P)"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handlePictureInPicture()
                }
              }}
            />
          )}
          <video
            className={styles.fullscreenVideo}
            ref={videoRef}
            src={videoData.url}
            type={videoData.mimeType}
            controls
            controlsList="nodownload noplaybackrate noremoteplayback"
            autoPlay
            onClick={handlePlayerClick}
            onLoadedData={() => setIsLoading(false)}
            onLoadStart={() => setIsLoading(true)}
            onEnded={handleClose}
            preload="metadata"
            playsInline
            aria-label={`Video: ${videoData.caption || 'Video content'}`}
          />
        </div>,
        document.body
      )}
    </>
  )
}

const MemoizedWaVideo = memo(WaVideo)
MemoizedWaVideo.displayName = 'WaVideo'

export default MemoizedWaVideo
